// frontend/src/pages/ProductDetailPage.jsx
// Product-first UX - Full detail page with tier ladder and social proof
// Updated: Token-based Share & Earn system (no flat $2 discount)
// Fixed: Add to Cart button in quantity section, BottomNav restored
// Fixed: Added cart icon, notifications, and token balance to header

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_PRODUCT_BY_ID } from '../apollo/queries';
import { 
  ChevronLeft, 
  Share2, 
  Clock, 
  Users, 
  Plus, 
  Minus, 
  ShoppingCart,
  MapPin,
  Leaf,
  Award,
  Copy,
  MessageCircle,
  Check,
  TrendingDown,
  Gift,
  Coins,
  Bell
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNotification } from '../context/NotificationContext';
import { useTokens } from '../context/TokenContext'; 
import BottomNav from '../components/common/BottomNav';
import { 
  TOKEN_CONFIG,
  calculatePurchaseTokens, 
  getShareEarnDisplay,
  getBulkBonusDisplay,
  formatTokens,
  formatTokenValue
} from '../utils/tokenEconomy';
// Group buying components
import TimeWindowSelector from '../components/purchase/TimeWindowSelector';
import GroupPricingDisplay from '../components/purchase/GroupPricingDisplay';
import { useAllWindowPricing } from '../graphql/groupBuying';



const ProductDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const passedProduct = location.state?.product;
  const navigate = useNavigate();
  const { addToCart, cartItems } = useCart();
  const { addNotification, notifications } = useNotification();

  
  
  // Get token balance from context
  const { balance: tokenBalance } = useTokens();
  
  // Calculate cart count
  const cartCount = cartItems?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;
  const unreadNotifications = notifications?.filter(n => !n.read)?.length || 0;
  
  const [quantity, setQuantity] = useState(1);
  const [showShareModal, setShowShareModal] = useState(false);
  const [now, setNow] = useState(new Date());
  const [imageError, setImageError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [selectedTimeWindow, setSelectedTimeWindow] = useState('THREE_DAY');

  // Fetch product data
  const { data, loading, error } = useQuery(GET_PRODUCT_BY_ID, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    skip: !!passedProduct
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update time for countdown
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const product = passedProduct || data?.product;

  // Fetch group buying pricing for all time windows
  const { pricing: windowPricing } = useAllWindowPricing(product?.id);

  // Get current pricing based on selected time window
  const currentGroupPricing = windowPricing?.[selectedTimeWindow] || null;

  // Calculate group metrics
  const groupData = useMemo(() => {
    if (product?.groups?.[0]) {
      return {
        buyersCount: product.groups[0].currentSize || 0,
        targetSize: product.groups[0].targetSize || 50,
        groupId: product.groups[0].id
      };
    }
    return { buyersCount: 0, targetSize: 50, groupId: null };
  }, [product]);

  const progress = groupData.targetSize > 0 
    ? Math.round((groupData.buyersCount / groupData.targetSize) * 100) 
    : 0;

// Deadline based on selected time window
  const timeWindowHours = {
    'EXPRESS': 12,
    'ONE_DAY': 24,
    'THREE_DAY': 72,
    'SEVEN_DAY': 168
  };
  const deadline = useMemo(() => 
    new Date(Date.now() + (timeWindowHours[selectedTimeWindow] || 72) * 60 * 60 * 1000), 
    [selectedTimeWindow]
  );

  // Time calculations
  const timeLeft = useMemo(() => {
    const diff = deadline - now;
    if (diff <= 0) return { expired: true };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { days, hours, minutes, expired: false };
  }, [deadline, now]);

  const formatTimeLeft = () => {
    if (!timeLeft || timeLeft.expired) return 'Ended';
    if (timeLeft.days > 0) return `${timeLeft.days}d ${timeLeft.hours}h`;
    return `${timeLeft.hours}h ${timeLeft.minutes}m`;
  };

  // Use group pricing if available, otherwise fall back to product price
  const displayPrice = currentGroupPricing?.currentPrice 
    || product?.currentPrice 
    || product?.basePrice 
    || 0;

  const basePrice = product?.basePrice || displayPrice;
  const groupDiscount = currentGroupPricing?.discount || 0;
  const buyerCount = currentGroupPricing?.buyerCount || groupData.buyersCount || 0;
  const priceTiers = product?.priceTiers || [];
  
  const sortedTiers = useMemo(() => 
    [...priceTiers].sort((a, b) => (a.groupSize || 0) - (b.groupSize || 0)), 
    [priceTiers]
  );

  const currentTier = useMemo(() => 
    sortedTiers.filter(t => (t.groupSize || 0) <= groupData.buyersCount).pop(),
    [sortedTiers, groupData.buyersCount]
  );

  const nextTier = useMemo(() => 
    sortedTiers.find(t => (t.groupSize || 0) > groupData.buyersCount),
    [sortedTiers, groupData.buyersCount]
  );

  // Token calculations using new system
  const tokenCalc = useMemo(() => 
    calculatePurchaseTokens({ quantity }),
    [quantity]
  );

  const bulkBonus = useMemo(() => 
    getBulkBonusDisplay(quantity),
    [quantity]
  );

  const shareEarnInfo = useMemo(() => 
    getShareEarnDisplay(),
    []
  );

  const totalPrice = displayPrice * quantity;
  const vendorName = typeof product?.vendor === 'string' 
    ? product.vendor 
    : product?.vendor?.name || 'Local Vendor';

  // Handle add to cart
  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      productId: product.id,
      name: product.name,
      price: displayPrice,
      basePrice: basePrice,
      quantity,
      image: product.images?.[0]?.full || product.images?.[0],
      vendor: vendorName,
      groupId: groupData.groupId,
      // Group buying fields
      timeWindow: selectedTimeWindow,
      groupDiscount: groupDiscount,
      buyerCount: buyerCount
    });
    addNotification({
      type: 'success',
      title: 'Added to Cart!',
      message: `${quantity}x ${product.name} added${groupDiscount > 0 ? ` • Saving ${groupDiscount}%!` : ''}`,
      showToast: true
    });
    navigate('/cart');
  };

  // Handle share
  const handleShare = async (method) => {
    // In production, this would include a referral code
    const referralCode = 'REF123'; // TODO: Get from user context
    const shareUrl = `${window.location.origin}/product/${id}?ref=${referralCode}`;
    const shareText = `Check out this deal on UniLedge: ${product?.name} - You'll get bonus tokens on your first order!`;
    
    switch(method) {
      case 'copy':
        await navigator.clipboard.writeText(shareUrl);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
        addNotification({ type: 'success', title: 'Link Copied!', message: 'Share with friends to earn tokens', showToast: true });
        break;
      case 'sms':
        window.open(`sms:?body=${encodeURIComponent(shareText + ' ' + shareUrl)}`);
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`);
        break;
      case 'native':
        if (navigator.share) {
          await navigator.share({ title: product?.name, text: shareText, url: shareUrl });
        }
        break;
      default:
        break;
    }
    setShowShareModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="text-6xl mb-4">😕</div>
        <h1 className="text-xl font-semibold text-gray-800 mb-2">Product Not Found</h1>
        <p className="text-gray-600 mb-4">This product may no longer be available.</p>
        <button onClick={() => navigate('/')} className="bg-blue-600 text-white px-6 py-2 rounded-lg">
          Back to Deals
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      {/* Header with Cart, Notifications, and Token Balance */}
      <div className="bg-white px-4 py-3 flex items-center justify-between shadow-sm sticky top-0 z-10">
        {/* Left: Back Button */}
        <button onClick={() => navigate(-1)} className="p-1">
          <ChevronLeft className="w-6 h-6 text-gray-600" />
        </button>
        
        {/* Right: Token Balance, Cart, Notifications, Share */}
        <div className="flex items-center gap-3">
          {/* Token Balance */}
          <button 
            onClick={() => navigate('/wallet')} 
            className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-full"
          >
            <Coins className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-700">
              {formatTokens(tokenBalance)}
            </span>
          </button>
          
          {/* Cart Icon */}
          <button onClick={() => navigate('/cart')} className="relative p-1">
            <ShoppingCart className="w-5 h-5 text-gray-600" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                {cartCount > 9 ? '9+' : cartCount}
              </span>
            )}
          </button>
          
          {/* Notifications Icon */}
          <button onClick={() => navigate('/notifications')} className="relative p-1">
            <Bell className="w-5 h-5 text-gray-600" />
            {unreadNotifications > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>
          
          {/* Share Button */}
          <button onClick={() => setShowShareModal(true)} className="p-1">
            <Share2 className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Product Image Carousel */}
      <div className="bg-white h-64 flex items-center justify-center relative">
        {(() => {
          const allImages = product.images?.length > 0 ? product.images : (product.image ? [{ full: product.image }] : []);
          const currentImg = allImages[currentImageIndex];
          
          if (!currentImg || imageError) {
            return <div className="text-8xl">📦</div>;
          }
          
          return (
            <>
              <img 
                src={currentImg.full || currentImg.thumbnail || currentImg} 
                alt={`${product.name} - ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
              
              {/* Image Navigation Dots */}
              {allImages.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {allImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                      className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white w-4' : 'bg-white/50'}`}
                    />
                  ))}
                </div>
              )}
              
              {/* Arrow Navigation */}
              {allImages.length > 1 && (
                <>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(i => i === 0 ? allImages.length - 1 : i - 1); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white w-8 h-8 rounded-full flex items-center justify-center"
                  >
                    ‹
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(i => i === allImages.length - 1 ? 0 : i + 1); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white w-8 h-8 rounded-full flex items-center justify-center"
                  >
                    ›
                  </button>
                </>
              )}
            </>
          );
        })()}
        <div className="absolute bottom-3 left-3 flex gap-2">
          {product.attributes?.organic && (
            <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <Leaf className="w-3 h-3" /> Organic
            </span>
          )}
          {product.attributes?.local && (
            <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Local
            </span>
          )}
        </div>
      </div>

      {/* Product Info */}
      <div className="bg-white px-4 py-4 border-t">
        {product.category && (
          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
            {product.category}
          </span>
        )}
        <h1 className="text-xl font-bold text-gray-900 mt-2">{product.name}</h1>
        <p className="text-gray-600 mt-1">{vendorName}</p>
        <div className="mt-3 flex items-baseline gap-3">
          <span className="text-3xl font-bold text-gray-900">${displayPrice.toFixed(2)}</span>
          {product.unit && product.unit !== 'each' && (
            <span className="text-gray-500">per {product.unit}</span>
          )}
        </div>
      </div>

      {/* Social Proof */}
      <div className="bg-blue-50 px-4 py-4 mt-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-blue-700">
            <Users className="w-5 h-5" />
            <span className="font-medium">
              {groupData.buyersCount + quantity === quantity 
                ? `Be the first! You're adding ${quantity} unit${quantity > 1 ? 's' : ''}` 
                : `${groupData.buyersCount + quantity} people buying (including you)`}
            </span>
          </div>
          <div className="flex items-center gap-1 text-gray-600 text-sm">
            <Clock className="w-4 h-4" />
            <span>Ends in {formatTimeLeft()}</span>
          </div>
        </div>
        
        {/* Progress toward next tier - dynamic based on current position */}
        {(() => {
          const totalBuyers = groupData.buyersCount + quantity;
          const currentTierForProgress = sortedTiers.filter(t => totalBuyers >= (t.groupSize || 0)).pop();
          const nextTierForProgress = sortedTiers.find(t => (t.groupSize || 0) > totalBuyers);
          const prevTierSize = currentTierForProgress?.groupSize || 0;
          
          if (nextTierForProgress) {
            const tierRange = nextTierForProgress.groupSize - prevTierSize;
            const progressInRange = totalBuyers - prevTierSize;
            const progressPercent = Math.min(Math.round((progressInRange / tierRange) * 100), 100);
            const needed = nextTierForProgress.groupSize - totalBuyers;
            
            return (
              <>
                <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all duration-300" 
                    style={{ width: `${Math.max(progressPercent, 2)}%` }} 
                  />
                </div>
                <p className="text-xs text-blue-600 mt-1 text-center">
                  {needed > 0 
                    ? `${needed} more needed to unlock $${nextTierForProgress.price?.toFixed(2)} price!`
                    : `🎉 You'll unlock the $${nextTierForProgress.price?.toFixed(2)} price!`}
                </p>
              </>
            );
          } else {
            return (
              <>
                <div className="h-2 bg-green-200 rounded-full overflow-hidden">
                  <div className="h-full bg-green-600 rounded-full" style={{ width: '100%' }} />
                </div>
                <p className="text-xs text-green-600 mt-1 text-center">
                  🎉 Best price unlocked!
                </p>
              </>
            );
          }
        })()}
      </div>

      {/* Price Tier Ladder */}
      {sortedTiers.length > 0 && (
        <div className="bg-white px-4 py-4 mt-2">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-green-500" />
            Price Drops With More Buyers
          </h3>
          <div className="space-y-2">
            {sortedTiers.map((tier, idx) => {
              const tierSize = tier.groupSize || 0;
              const totalBuyers = groupData.buyersCount + quantity;
              const isUnlocked = totalBuyers >= tierSize;
              const currentTierWithQty = sortedTiers.filter(t => totalBuyers >= (t.groupSize || 0)).pop();
              const nextTierWithQty = sortedTiers.find(t => (t.groupSize || 0) > totalBuyers);
              const isCurrent = currentTierWithQty?.groupSize === tierSize;
              const isNext = nextTierWithQty?.groupSize === tierSize;
              const needed = tierSize - totalBuyers;
              const firstPrice = sortedTiers[0]?.price || displayPrice;
              const pctOff = firstPrice > 0 ? Math.round((1 - tier.price / firstPrice) * 100) : 0;
              
              return (
                <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${
                  isCurrent ? 'bg-green-50 border-2 border-green-500' 
                  : isNext ? 'bg-blue-50 border border-blue-300'
                  : isUnlocked ? 'bg-gray-50 opacity-60' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center gap-3">
                    {isUnlocked ? <Check className={`w-5 h-5 ${isCurrent ? 'text-green-600' : 'text-gray-400'}`} />
                    : <span className="text-lg">{isNext ? '🔓' : '🔒'}</span>}
                    <div>
                      <span className={`font-medium ${isCurrent ? 'text-green-700' : isNext ? 'text-blue-700' : 'text-gray-700'}`}>
                        ${tier.price.toFixed(2)}
                      </span>
                      {pctOff > 0 && <span className="ml-2 text-xs text-green-600">-{pctOff}%</span>}
                      {isCurrent && <span className="ml-2 text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Current</span>}
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">
                    {isUnlocked ? `${tierSize}+ ✓` : isNext ? <span className="text-blue-600 font-medium">{needed} more!</span> : `${tierSize}+`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Share to Save CTA - Animated */}
      <div className="mx-4 mt-2">
        <div 
          className="relative overflow-hidden px-4 py-4 rounded-lg border-2 border-purple-300"
          style={{
            background: 'linear-gradient(-45deg, #e0e7ff, #ddd6fe, #c7d2fe, #e9d5ff)',
            backgroundSize: '400% 400%',
            animation: 'gradient-pulse 3s ease infinite'
          }}
        >
          <style>
            {`
              @keyframes gradient-pulse {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
              }
            `}
          </style>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-purple-800 flex items-center gap-2 text-lg">
                🔗 Share & Save More!
              </h3>
              <p className="text-sm text-purple-700 mt-1">
                Every friend who joins drops YOUR price + YOU earn tokens!
              </p>
            </div>
            <button
              onClick={() => {
                const shareUrl = `${window.location.origin}/product/${product._id || product.id}`;
                if (navigator.share) {
                  navigator.share({
                    title: product.name,
                    text: `Join my group buy and we both save! ${product.name}`,
                    url: shareUrl
                  });
                } else {
                  navigator.clipboard.writeText(shareUrl);
                  alert('Link copied! Share with friends to unlock more savings.');
                }
              }}
              className="bg-purple-600 text-white px-5 py-3 rounded-lg font-bold hover:bg-purple-700 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform"
            >
              Share Now
            </button>
          </div>
        </div>
      </div>

      {/* Delivery Time Window Selector */}
      <TimeWindowSelector
          selected={selectedTimeWindow}
          onSelect={setSelectedTimeWindow}
        />

      {/* Quantity Selector with Add to Cart Button */}
      <div className="bg-white px-4 py-4 mt-2">
        <div className="flex items-center justify-between mb-4">
          <span className="font-medium">Quantity</span>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setQuantity(q => Math.max(1, q - 1))} 
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 active:bg-gray-300"
            >
              <Minus className="w-5 h-5" />
            </button>
            <span className="text-xl font-bold w-8 text-center">{quantity}</span>
            <button 
              onClick={() => setQuantity(q => q + 1)} 
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 active:bg-gray-300"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Bulk Hero Badge */}
        {bulkBonus.isBonus && (
          <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-orange-700">
              <Award className="w-5 h-5" />
              <span className="font-medium">{bulkBonus.currentMessage}</span>
            </div>
          </div>
        )}
        
        {/* Next Bonus Hint */}
        {!bulkBonus.isBonus && bulkBonus.nextBonusAt && (
          <p className="mb-4 text-sm text-gray-500 text-center">
            {bulkBonus.nextMessage}
          </p>
        )}
        {bulkBonus.isBonus && bulkBonus.nextBonusAt && (
          <p className="mb-4 text-sm text-orange-600 text-center">
            {bulkBonus.nextMessage}
          </p>
        )}

        {/* Price and Token Summary */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Total:</span>
            <span className="text-2xl font-bold">${totalPrice.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center mt-1">
            <span className="text-yellow-600 text-sm flex items-center gap-1">
              <Coins className="w-4 h-4" />
              Tokens to earn:
            </span>
            <span className="text-yellow-600 font-medium">
              +{formatTokens(tokenCalc.totalTokens)} ({formatTokenValue(tokenCalc.totalTokens)})
            </span>
          </div>
          {tokenCalc.bulkBonus > 0 && (
            <p className="text-xs text-orange-600 text-right mt-1">
              Includes +{formatTokens(tokenCalc.bulkBonus)} bulk bonus!
            </p>
          )}
        </div>

        {/* ADD TO CART BUTTON - Now in the quantity section */}
        <button 
          onClick={handleAddToCart} 
          className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 hover:bg-blue-700 active:bg-blue-800 transition-colors"
        >
          <ShoppingCart className="w-5 h-5" />
          Add to Cart
        </button>
      </div>

      {/* Description */}
      {product.description && (
        <div className="bg-white px-4 py-4 mt-2">
          <h3 className="font-semibold mb-2">About This Product</h3>
          <p className="text-gray-600 text-sm">{product.description}</p>
        </div>
      )}

      {/* Share & Earn - TOKEN BASED */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-4 py-4 mt-2">
        <h3 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
          <Share2 className="w-5 h-5" /> Share & Earn Tokens
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          {shareEarnInfo.recurringNote}
        </p>
        <div className="flex gap-2 mb-3">
          {/* Your Reward */}
          <div className="flex-1 bg-white rounded-lg p-3 text-center border border-purple-200">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Coins className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-purple-600 font-medium">YOU EARN</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{shareEarnInfo.sharerTokens}</p>
            <p className="text-xs text-gray-500">tokens ({shareEarnInfo.sharerValue})</p>
            <p className="text-xs text-purple-600 mt-1">per referral!</p>
          </div>
          {/* Friend's Reward */}
          <div className="flex-1 bg-white rounded-lg p-3 text-center border border-green-200">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Gift className="w-4 h-4 text-green-500" />
              <span className="text-xs text-green-600 font-medium">FRIEND GETS</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{shareEarnInfo.friendTokens}</p>
            <p className="text-xs text-gray-500">bonus tokens ({shareEarnInfo.friendValue})</p>
            <p className="text-xs text-green-600 mt-1">on their order!</p>
          </div>
        </div>
        <button 
          onClick={() => setShowShareModal(true)} 
          className="w-full bg-purple-600 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-purple-700"
        >
          <Share2 className="w-4 h-4" />
          Share This Deal
        </button>
      </div>

      {/* Share Modal - Updated with token messaging */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end" onClick={() => setShowShareModal(false)}>
          <div className="bg-white w-full rounded-t-2xl p-4" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Share & Earn Tokens</h3>
              <button onClick={() => setShowShareModal(false)} className="text-gray-500 text-xl">✕</button>
            </div>
            
            {/* Token Rewards Explanation */}
            <div className="bg-gradient-to-r from-purple-50 to-green-50 rounded-lg p-4 mb-4">
              <p className="text-gray-700 text-center font-medium mb-3">When friends buy through your link:</p>
              <div className="flex justify-center gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-1">
                    <Coins className="w-6 h-6 text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold text-purple-600">+{shareEarnInfo.sharerTokens}</p>
                  <p className="text-xs text-gray-500">tokens for you</p>
                  <p className="text-xs text-purple-600">{shareEarnInfo.sharerValue}</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1">
                    <Gift className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-green-600">+{shareEarnInfo.friendTokens}</p>
                  <p className="text-xs text-gray-500">tokens for them</p>
                  <p className="text-xs text-green-600">{shareEarnInfo.friendValue}</p>
                </div>
              </div>
              <p className="text-xs text-center text-gray-500 mt-3 bg-white/50 rounded-full py-1 px-2">
                ♻️ Recurring! Earn every time someone uses your link
              </p>
            </div>
            
            {/* Share Buttons */}
            <div className="grid grid-cols-4 gap-3">
              <button onClick={() => handleShare('copy')} className="flex flex-col items-center p-3 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                {linkCopied ? <Check className="w-6 h-6 text-green-600 mb-1" /> : <Copy className="w-6 h-6 text-gray-600 mb-1" />}
                <span className="text-xs">{linkCopied ? 'Copied!' : 'Copy'}</span>
              </button>
              <button onClick={() => handleShare('sms')} className="flex flex-col items-center p-3 bg-green-100 rounded-xl hover:bg-green-200 transition-colors">
                <MessageCircle className="w-6 h-6 text-green-600 mb-1" /><span className="text-xs">SMS</span>
              </button>
              <button onClick={() => handleShare('whatsapp')} className="flex flex-col items-center p-3 bg-green-500 rounded-xl hover:bg-green-600 transition-colors">
                <span className="text-xl mb-1">📱</span><span className="text-xs text-white">WhatsApp</span>
              </button>
              <button onClick={() => handleShare('native')} className="flex flex-col items-center p-3 bg-blue-100 rounded-xl hover:bg-blue-200 transition-colors">
                <Share2 className="w-6 h-6 text-blue-600 mb-1" /><span className="text-xs">More</span>
              </button>
            </div>
            
            {/* Use tokens reminder */}
            <p className="text-xs text-center text-gray-500 mt-4">
              Tokens can be used for discounts on future purchases!
            </p>
          </div>
        </div>
      )}

      {/* BottomNav - Restored */}
      <BottomNav />
    </div>
  );
};

export default ProductDetailPage;
